import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly token_key = 'bisual-token';
  private readonly refresh_key = 'bisual-refresh-token';
  private readonly allowed_audiences = [environment.oauth_client_id, environment.oauth_personal_access_client_id];
  private readonly refresh_before_expiry_ms = 30_000;

  handle(token: string, refresh_token: string | null = null): void {
    this.persist(token, refresh_token, false);
  }

  handleParent(token: string): void {
    this.persist(token, this.getRefreshToken(), true);
  }

  loggedIn(): boolean {
    return this.isUsable(true);
  }

  hasAttachableAccessToken(): boolean {
    return this.isUsable(false);
  }

  getParent(): string | null {
    return localStorage.getItem(this.storageKey(this.token_key, true));
  }

  get(): string | null {
    return localStorage.getItem(this.storageKey(this.token_key, false));
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.storageKey(this.refresh_key, false));
  }

  public remove(): void {
    this.clear(false);
  }

  public removeParent(): void {
    this.clear(true);
  }

  restoreParentSession(): void {
    const parent_token = this.getParent();
    if (!parent_token) {
      return;
    }

    this.handle(parent_token, localStorage.getItem(this.storageKey(this.refresh_key, true)));
    this.removeParent();
  }

  private persist(token: string, refresh_token: string | null, parent: boolean): void {
    this.set(token, parent);

    const refresh_storage_key = this.storageKey(this.refresh_key, parent);
    if (refresh_token) {
      localStorage.setItem(refresh_storage_key, refresh_token);
    } else if (!parent) {
      localStorage.removeItem(refresh_storage_key);
    }
  }

  private set(token: string, parent = false): void {
    localStorage.setItem(this.storageKey(this.token_key, parent), token);
  }

  private clear(parent: boolean): void {
    localStorage.removeItem(this.storageKey(this.token_key, parent));
    localStorage.removeItem(this.storageKey(this.refresh_key, parent));
  }

  private storageKey(base: string, parent: boolean): string {
    return parent ? `${base}-parent` : base;
  }

  private isUsable(refresh_early: boolean): boolean {
    const token = this.get();
    if (!token) {
      return false;
    }

    const payload = this.payload(token);
    if (!payload || typeof payload.exp !== 'number' || !this.hasAllowedAudience(payload.aud)) {
      return false;
    }

    const early_ms = refresh_early ? this.refresh_before_expiry_ms : 0;

    return Date.now() < payload.exp * 1000 - early_ms;
  }

  private hasAllowedAudience(aud: string | string[] | undefined): boolean {
    if (typeof aud === 'string') {
      return this.allowed_audiences.includes(aud);
    }

    if (Array.isArray(aud)) {
      return aud.some(value => this.allowed_audiences.includes(value));
    }

    return false;
  }

  private payload(token: string): { exp?: number; aud?: string | string[] } | null {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    return this.decode(parts[1]);
  }

  private decode(segment: string): { exp?: number; aud?: string | string[] } | null {
    try {
      const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
      return JSON.parse(atob(padded)) as { exp?: number; aud?: string | string[] };
    } catch {
      return null;
    }
  }
}
